import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createManagerWorker, sendTestUserOnboardedNotification } from 'src/services/api';
import { useAuth } from 'src/contexts/AuthContext';
import { logger } from 'src/utils/logger';

interface Worker {
    id: string;
    name: string;
    email: string;
    role: string;
    status: 'active' | 'inactive';
    created_at: string;
    last_login_at?: string | null;
}

interface AddNewUserModalProps {
    show: boolean;
    onClose: () => void;
    onCreated?: (worker: Worker) => void;
}

export const AddNewUserModal: React.FC<AddNewUserModalProps> = ({ show, onClose, onCreated }) => {
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [emailError, setEmailError] = useState<string>("");
    const [checkingEmail, setCheckingEmail] = useState(false);
    const { user } = useAuth();

    // Validation errors
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Reset all form data and errors when modal closes
    const resetForm = () => {
        setUserName("");
        setUserEmail("");
        setEmailError("");
        setCheckingEmail(false);
        setIsSaving(false);
        setErrors({});
    };

    // Validation functions
    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!userName.trim()) {
            newErrors.name = 'User Name is required';
        }

        if (!userEmail.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(userEmail)) {
            newErrors.email = 'Email is invalid';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'name') {
            if (!value.trim()) {
                newErrors.name = 'User Name is required';
            } else {
                delete newErrors.name;
            }
        } else if (fieldName === 'email') {
            if (!value.trim()) {
                newErrors.email = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(value)) {
                newErrors.email = 'Email is invalid';
            } else {
                delete newErrors.email;
            }
        }

        setErrors(newErrors);
    };

    // Debounced email check
    useEffect(() => {
        if (!userEmail.trim()) {
            setEmailError("");
            return;
        }

        const timeoutId = setTimeout(() => {
            checkEmailExists(userEmail);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [userEmail]);

    // Check if email exists
    const checkEmailExists = async (email: string) => {
        if (!email.trim()) {
            setEmailError("");
            return false;
        }

        try {
            setCheckingEmail(true);
            setEmailError("");
            // Note: We don't have access to fetchUsers in manager context
            // This is a simplified check - in real implementation you'd need API access
            setEmailError("");
            return false;
        } catch (error) {
            logger.error('Error checking email:', error);
            setEmailError("Error checking email");
            return false;
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Check if email already exists
        const emailExists = await checkEmailExists(userEmail.trim());
        if (emailExists) {
            return; // Error message is already set by checkEmailExists
        }

        setIsSaving(true);
        setErrors({});

        try {
            const response = await createManagerWorker({
                name: userName.trim(),
                email: userEmail.trim(),
            });

            // Create a worker object from the response
            const newWorker: Worker = {
                id: response.item?.id || Date.now().toString(),
                name: userName.trim(),
                email: userEmail.trim(),
                role: 'worker',
                status: 'active',
                created_at: response.item?.created_at || new Date().toISOString(),
            };

            // Send notification to admins about user onboarding
            try {
                await sendTestUserOnboardedNotification({
                    manager_name: user?.full_name || 'Unknown Manager',
                    onboarded_user_name: userName.trim(),
                    onboarded_user_email: userEmail.trim(),
                    onboarded_user_role: 'worker'
                });
            } catch (notificationError) {
                logger.error('Failed to send admin notification:', notificationError);
                // Don't fail the user creation if notification fails
            }

            toast.success("User created successfully!");
            onCreated?.(newWorker);
            resetForm();
            onClose();
        } catch (e: any) {
            logger.error('Failed to create worker', e);
            const errorMessage = e?.message || "Please contact your admin. You are not allowed to create users at the moment.";
            setErrors({ general: errorMessage });
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={() => { resetForm(); onClose(); }} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title>
                    Add New User
                </Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={() => { resetForm(); onClose(); }}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <form id="add-user-form" onSubmit={handleSubmit}>
                    {/* User Name */}
                    <div className="form-group">
                        <label htmlFor="userName">User Name</label>
                        <input
                            id="userName"
                            type="text"
                            value={userName}
                            onChange={(e) => {
                                setUserName(e.target.value);
                                validateField('name', e.target.value);
                            }}
                            className={`form-control ${errors.name ? 'border-danger' : ''}`}
                            placeholder="Enter user name"
                        />
                        {errors.name && (
                            <p className="error-message">{errors.name}</p>
                        )}
                    </div>

                    {/* User Email ID */}
                    <div className="form-group">
                        <label htmlFor="userEmail">User Email ID</label>
                        <div className="position-relative">
                            <input
                                id="userEmail"
                                type="email"
                                value={userEmail}
                                onChange={(e) => {
                                    setUserEmail(e.target.value);
                                    validateField('email', e.target.value);
                                }}
                                className={`form-control ${errors.email || emailError ? 'border-danger' : ''}`}
                                placeholder="Enter user email"
                            />
                            {checkingEmail && (
                                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                    <Spinner size="sm" />
                                </div>
                            )}
                        </div>
                        {(errors.email || emailError) && (
                            <p className="error-message">{errors.email || emailError}</p>
                        )}
                    </div>

                    {/* Informational Text */}
                    <div className="text-16-reg grey-700 bg-[#F7F7F7] rounded-[10px] mb-3 p-3">
                        Role will be automatically set to <b>Field Worker</b> and assigned to your management.
                    </div>

                    {/* General Error Message */}
                    {/* {errors.general && (
                        <div className="alert alert-danger" role="alert">
                            {errors.general}
                        </div>
                    )} */}
                </form>
                <div className="d-flex justify-content-end gap-3 mt-4">
                    <button
                        type="button"
                        onClick={() => { resetForm(); onClose(); }}
                        className="renegade-btn-secondary mini-btn"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="add-user-form"
                        disabled={isSaving}
                        className="renegade-btn-primary mini-btn"
                        onClick={handleSubmit}
                    >
                        {isSaving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Adding User...
                            </>
                        ) : (
                            'Add New User'
                        )}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AddNewUserModal;
